<template>
  <div class="expiration-contract-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('expirationContractManage.list.title')" :subtitle="$t('expirationContractManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.contractNameLike" clearable
            :placeholder="$t('expirationContractManage.search.contractNameLike')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.contractCode" clearable
            :placeholder="$t('expirationContractManage.search.contractCode')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.contractType" clearable class="w-full"
            :placeholder="$t('expirationContractManage.search.contractType')">
            <el-option v-for="item in contractTypes" :key="item.contractTypeId" :label="item.typeName"
              :value="item.contractTypeId" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('expirationContractManage.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('expirationContractManage.page.empty')" @retry="getList">
        <el-table-column prop="contractName" :label="$t('expirationContractManage.table.contractName')" align="left" min-width="160" show-overflow-tooltip />
        <el-table-column prop="contractCode" :label="$t('expirationContractManage.table.contractCode')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.contractCode || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="contractTypeName" :label="$t('expirationContractManage.table.contractType')" align="center" width="120" />
        <el-table-column prop="partyA" :label="$t('expirationContractManage.table.partyA')" align="center" min-width="130" show-overflow-tooltip />
        <el-table-column prop="partyB" :label="$t('expirationContractManage.table.partyB')" align="center" min-width="130" show-overflow-tooltip />
        <el-table-column prop="operator" :label="$t('expirationContractManage.table.operator')" align="center" width="110" />
        <el-table-column prop="amount" :label="$t('expirationContractManage.table.amount')" align="right" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="startTime" :label="$t('expirationContractManage.table.startTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.startTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="endTime" :label="$t('expirationContractManage.table.endTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.endTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('expirationContractManage.table.status')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName" :text="scope.row.stateName" type="info" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleRenew(scope.row)">
              {{ $t('expirationContractManage.button.renew') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleStop(scope.row)">
              {{ $t('expirationContractManage.button.stop') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 终止合同弹窗 -->
    <stop-contract ref="stopContractDialog" @success="handleSuccess" />
  </div>
</template>

<script>
import { queryContract, queryContractType } from '@/api/contract/expirationContractManageApi'
import StopContract from '@/components/contract/StopContract'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ExpirationContractManageList',
  components: {
    StopContract,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        contractNameLike: '',
        contractCode: '',
        contractType: '',
        expiration: '1'
      },
      tableData: [],
      contractTypes: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
    this.getContractTypes()
  },
  methods: {
    formatMoney(value) {
      return formatMoney(value)
    },
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          ...this.searchForm
        }
        const { data, total } = await queryContract(params)
        this.tableData = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取到期合同列表失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('expirationContractManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    async getContractTypes() {
      try {
        const { data } = await queryContractType({
          page: 1,
          row: 50
        })
        this.contractTypes = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('获取合同类型失败:', error)
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        contractNameLike: '',
        contractCode: '',
        contractType: '',
        expiration: '1'
      }
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    handleRenew(row) {
      this.$router.push({
        path: '/views/contract/addContract',
        query: {
          contractId: row.contractId,
          contractCode: row.contractCode,
          contractName: row.contractName,
          stateName: row.stateName
        }
      })
    },
    handleStop(row) {
      this.$refs.stopContractDialog.open(row)
    },
    handleSuccess() {
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.expiration-contract-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
